import React, { useMemo } from "react"
import Image from "next/image"
import { rem } from "polished"
import { GetStaticProps, NextPage } from "next"
import { StructuredText } from "react-datocms"
import styled from "styled-components"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { Row } from "react-styled-flexboxgrid"
import { MainLayout } from "../components/layouts/main-layout/MainLayout"
import { SingleColLayout } from "../components/single-col-layout/SingleColLayout"
import { HeroBlock } from "../datocms/components/HeroBlock"
import { nextLocaleToDatoLocale } from "../datocms/dato-utils"
import { datoClient } from "../datocms/gql/client/apollo-client"
import {
  GetAboutPageDocument,
  GetAboutPageQuery,
  GetAboutPageQueryVariables,
} from "../datocms/gql/generated/graphql"
import {
  omitParagraphTagRenderRule,
  paragraphRenderRule,
} from "../datocms/nodeRules"
import { BasePageProps, NEXT_JS_ISR_REVALIDATE } from "../util/page"
import { theme } from "../util/styles/theme"
import { Heading } from "../components/heading/Heading"
import { NextStepsSection } from "../components/next-steps-section/NextStepsSection"
import { Section } from "../components/section/Section"

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const page = await datoClient(preview).query<
    GetAboutPageQuery,
    GetAboutPageQueryVariables
  >({
    query: GetAboutPageDocument,
    variables: {
      locale: nextLocaleToDatoLocale(locale),
    },
  })

  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
      footer: page.data.footer,
      page: page.data.aboutPage,
      titleSuffix: page.data.site.globalSeo.titleSuffix,
    },
    revalidate: NEXT_JS_ISR_REVALIDATE,
  }
}

const originalImageSizePx = {
  height: 500,
  width: 1250,
}

const imageDisplayHeightPx = {
  md: 500,
  sm: 500,
  xs: 345,
}

const SectionImageContainer = styled.div`
  height: ${rem(imageDisplayHeightPx.sm)};
  width: ${rem(
    `${(
      (imageDisplayHeightPx.sm / originalImageSizePx.height) *
      originalImageSizePx.width
    ).toPrecision(4)}px`,
  )};
  margin-left: auto;
  margin-right: auto;

  ${theme.breakpoints.xs} {
    height: ${rem(imageDisplayHeightPx.xs)};
    width: ${rem(
      `${(
        (imageDisplayHeightPx.xs / originalImageSizePx.height) *
        originalImageSizePx.width
      ).toPrecision(4)}px`,
    )};
  }

  ${theme.breakpoints.sm} {
    height: ${rem(imageDisplayHeightPx.xs)};
    width: ${rem(
      `${(
        (imageDisplayHeightPx.sm / originalImageSizePx.height) *
        originalImageSizePx.width
      ).toPrecision(4)}px`,
    )};
  }

  ${theme.breakpoints.md} {
    height: ${rem(imageDisplayHeightPx.md)};
    width: ${rem(
      `${(
        (imageDisplayHeightPx.md / originalImageSizePx.height) *
        originalImageSizePx.width
      ).toPrecision(4)}px`,
    )};
  }
`

type AboutProps = {
  page: GetAboutPageQuery["aboutPage"]
  titleSuffix: string
} & BasePageProps

const About: NextPage<AboutProps> = ({ page, titleSuffix, footer }) => {
  const heroModel = page.hero[0]

  const hero = heroModel && <HeroBlock model={heroModel} />
  const intro = page.intro[0]
  const paragraphRenderNodeRules = useMemo(() => {
    return [
      paragraphRenderRule({
        size: "large",
      }),
    ]
  }, [])
  const story = page.story[0]
  const outro = page.outro[0]

  return (
    <MainLayout
      type="metaTags"
      titleSuffix={titleSuffix}
      heroComponent={hero}
      topBackground="white"
      bottomBackground="yellow"
      footer={footer}
      metaTags={page.seoMetaTags}
    >
      <Section backgroundColor="white">
        <SingleColLayout
          colProps={{
            xs: 12,
            xsOffset: 0,
            sm: 10,
            smOffset: 1,
            md: 8,
            mdOffset: 2,
          }}
        >
          <Heading as="h2" size="xlarge" align="center">
            <StructuredText
              data={intro.header.value}
              customNodeRules={[omitParagraphTagRenderRule]}
            />
          </Heading>
          <StructuredText
            data={intro.text.value}
            customNodeRules={paragraphRenderNodeRules}
          />
        </SingleColLayout>
      </Section>
      <Section backgroundColor="white">
        <Row>
          <SectionImageContainer>
            <Image
              src={page.storyImage.url}
              alt="Section photo"
              layout="responsive"
              height={page.storyImage.height}
              width={page.storyImage.width}
              priority
            />
          </SectionImageContainer>
        </Row>
        <SingleColLayout
          colProps={{
            xs: 12,
            xsOffset: 0,
            sm: 10,
            smOffset: 1,
            md: 8,
            mdOffset: 2,
          }}
        >
          <Heading as="h3" size="xlarge" align="center">
            <StructuredText
              data={story.header.value}
              customNodeRules={[omitParagraphTagRenderRule]}
            />
          </Heading>
          <StructuredText
            data={story.text.value}
            customNodeRules={paragraphRenderNodeRules}
          />
        </SingleColLayout>
      </Section>
      <Section backgroundColor="white">
        <Row>
          <SectionImageContainer>
            <Image
              src={page.outroImage.url}
              alt="Section photo"
              layout="responsive"
              height={page.outroImage.height}
              width={page.outroImage.width}
              priority
            />
          </SectionImageContainer>
        </Row>
        <SingleColLayout
          colProps={{
            xs: 12,
            xsOffset: 0,
            sm: 10,
            smOffset: 1,
            md: 8,
            mdOffset: 2,
          }}
        >
          <Heading as="h3" size="xlarge" align="center">
            <StructuredText
              data={outro.header.value}
              customNodeRules={[omitParagraphTagRenderRule]}
            />
          </Heading>
          <StructuredText
            data={outro.text.value}
            customNodeRules={paragraphRenderNodeRules}
          />
        </SingleColLayout>
      </Section>
      <Section backgroundColor="yellow">
        <NextStepsSection />
      </Section>
    </MainLayout>
  )
}

export default About
